import { EventIdentifier, Logger, TrackingEventType, useAnalytics, useSettings } from '@capsicum/common';
import { useWCPledgeClaimCancelledPledgePost } from '@capsicum/features/world-championship-pledge/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useTransactionErrorSnackbar } from '@capsicum/socios-core-web';
import { useBrowserTransaction } from '@socios-blk/browser';
import { type PlatformTransactionExecutionParams } from '@socios-blk/core';
import { useEffect } from 'react';

type UseWCPledgeBatchClaimTransactionParams = {
	campaignId?: string;
	claimablePledgeItems: Array<{
		ftName: string;
		teamId: string;
	}>;
	onTransactionError?: (error: Error) => void;
	onTransactionSuccess?: () => void;
	pledgeContractAddress?: string;
};

export const useWCPledgeBatchClaimTransaction = ({
	campaignId,
	claimablePledgeItems,
	onTransactionError,
	onTransactionSuccess,
	pledgeContractAddress
}: UseWCPledgeBatchClaimTransactionParams) => {
	const {
		settings: { isTransactionSponsored }
	} = useSettings();
	const { showTransactionErrorSnackbar } = useTransactionErrorSnackbar();
	const { formatCHZtoFiat } = useNCWBalance();
	const { estimateGasFee, gasFeeEstimate, isError, isPending, sendTransaction, TRANSACTIONS } =
		useBrowserTransaction();
	const analytics = useAnalytics();

	const gasFeeEstimateFiat = typeof gasFeeEstimate === 'number' ? formatCHZtoFiat(gasFeeEstimate) : undefined;

	const { claimCancelledPledgeMutation, isClaimCancelledPledgePosting } = useWCPledgeClaimCancelledPledgePost();

	/**
	 * The paymaster parameters required to sponsor the transaction.
	 */
	const paymasterParams: PlatformTransactionExecutionParams['paymasterParams'] = {
		isPaymasterConfigurationEnabled: isTransactionSponsored.worldChampionshipPledge,
		paymasterWebhookSecretParams: {
			parameters: [],
			target: 'world_championship_pledge'
		}
	};

	const buildClaimCancelledPledgeTransaction = () => {
		if (!pledgeContractAddress || !claimablePledgeItems.length) {
			return undefined;
		}

		const batchedClaimTransactions = claimablePledgeItems.map(({ ftName }) =>
			TRANSACTIONS.CLAIM_CANCELLED_PLEDGE({
				ftName,
				pledgeContractAddress
			})
		);

		return batchedClaimTransactions;
	};

	const batchedClaimCancelledPledgeTransaction = buildClaimCancelledPledgeTransaction();

	useEffect(() => {
		if (gasFeeEstimate || !batchedClaimCancelledPledgeTransaction || isPending || isError) {
			return;
		}

		estimateGasFee({
			paymasterParams,
			transaction: batchedClaimCancelledPledgeTransaction
		});
	}, [gasFeeEstimate, batchedClaimCancelledPledgeTransaction, isPending, isError]);

	const sendClaimCancelledPledgeTransaction = async () => {
		if (!batchedClaimCancelledPledgeTransaction) {
			showTransactionErrorSnackbar();

			Logger.error(new Error('Claim Cancelled Pledge Transaction is undefined'));

			return;
		}

		await sendTransaction({
			onError: err => {
				onTransactionError?.(err);
				showTransactionErrorSnackbar();
				Logger.error(new Error(`Claim Cancelled Pledge Transaction error: ${err}`));
			},
			onSuccess: async ok => {
				await claimCancelledPledgeMutation({
					campaignId: campaignId ?? '',
					teamIds: claimablePledgeItems.map(({ teamId }) => teamId),
					transactionHash: ok.transactionHash
				}).catch(error =>
					Logger.error('Error posting claim cancelled pledges to BE', {
						campaignId: campaignId,
						error: error,
						transactionHash: ok.transactionHash
					})
				);
				onTransactionSuccess?.();
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
					action: 'wc_pledge_claim',
					partner_name: claimablePledgeItems
				});
			},
			paymasterParams,
			transaction: batchedClaimCancelledPledgeTransaction
		});
	};

	return {
		gasFeeEstimate,
		gasFeeEstimateFiat,
		isTransactionPending: isPending || isClaimCancelledPledgePosting,
		sendClaimCancelledPledgeTransaction
	};
};
